import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Datastore, DatastoreCollection } from '@freelancer/datastore';
import {
  ReviewsCollection,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { AnchorScroll } from '@freelancer/ui';
import { ButtonColor } from '@freelancer/ui/button';
import { Margin } from '@freelancer/ui/margin';
import {
  FontStyle,
  FontWeight,
  TextAlign,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { isDefined } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { UserProfileSection } from '../../user-profile.component';

interface FeaturedReviewItem {
  readonly description: string;
  readonly reviewerName: string;
}

@Component({
  selector: 'app-user-profile-summary-featured-review',
  template: `
    <ng-container *ngIf="isViewerProfileOwner || (reviewItem$ | flAsync)">
      <fl-bit
        *ngIf="!(loadingReview$ | flAsync); else loading"
        class="FeaturedReview"
      >
        <fl-text
          [weight]="FontWeight.BOLD"
          [size]="TextSize.SMALL"
          [textTransform]="TextTransform.UPPERCASE"
          [textAlign]="TextAlign.LEFT"
          i18n="Featured review"
        >
          featured review
        </fl-text>
        <ng-container
          *ngIf="
            reviewItem$ | flAsync as reviewItem;
            else defaultFeaturedReview
          "
        >
          <fl-text [style]="FontStyle.ITALIC" [textAlign]="TextAlign.LEFT">
            "{{ reviewItem.description }}"
          </fl-text>
          <fl-text
            [flMarginBottom]="Margin.SMALL"
            [style]="FontStyle.ITALIC"
            [textAlign]="TextAlign.LEFT"
            [weight]="FontWeight.BOLD"
          >
            - {{ reviewItem.reviewerName }}
          </fl-text>

          <fl-button
            *ngIf="isViewerProfileOwner"
            i18n="Change Featured Review Button"
            flTrackingLabel="EditProfileChangeFeaturedReviewButtonClick"
            [color]="ButtonColor.SECONDARY"
            (click)="scrollToReviews()"
          >
            Change Featured Review
          </fl-button>
        </ng-container>
        <ng-template #defaultFeaturedReview>
          <fl-text
            i18n="Featured Review Default Text"
            [flMarginBottom]="Margin.SMALL"
            [textAlign]="TextAlign.LEFT"
          >
            Showcase your most valued customer feedback by setting a featured
            review.
          </fl-text>
          <fl-button
            *ngIf="isViewerProfileOwner"
            i18n="Set Featured Review Button"
            flTrackingLabel="EditProfileSetFeaturedReviewButtonClick"
            [color]="ButtonColor.SECONDARY"
            (click)="scrollToReviews()"
          >
            Set Featured Review
          </fl-button>
        </ng-template>
      </fl-bit>
      <ng-template #loading>
        <fl-loading-text [rows]="4"></fl-loading-text>
      </ng-template>
    </ng-container>
  `,
  styleUrls: ['./user-profile-summary-featured-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileSummaryFeaturedReviewComponent implements OnInit {
  ButtonColor = ButtonColor;
  FontStyle = FontStyle;
  FontWeight = FontWeight;
  Margin = Margin;
  TextAlign = TextAlign;
  TextSize = TextSize;
  TextTransform = TextTransform;

  @Input() featuredReviewsCollection: DatastoreCollection<ReviewsCollection>;
  @Input() isViewerProfileOwner: boolean;

  @Output() featuredReviewClick = new EventEmitter();

  reviewItem$: Rx.Observable<FeaturedReviewItem | undefined>;
  loadingReview$: Rx.Observable<boolean>;

  constructor(
    private anchorScroll: AnchorScroll,
    private datastore: Datastore,
  ) {}

  ngOnInit(): void {
    this.loadingReview$ = this.featuredReviewsCollection.status$.pipe(
      map(status => !status.ready),
    );

    const featuredReview$ = this.featuredReviewsCollection
      .valueChanges()
      .pipe(map(reviews => reviews.find(review => review.featured)));

    const reviewerUserDocument = this.datastore.document<UsersCollection>(
      'users',
      featuredReview$.pipe(
        filter(isDefined),
        map(review => review.fromUserId),
      ),
    );

    this.reviewItem$ = Rx.combineLatest([
      featuredReview$,
      reviewerUserDocument.valueChanges(),
    ]).pipe(
      map(([review, reviewer]) => {
        if (!review) {
          return undefined;
        }
        return {
          description: review.description,
          reviewerName: reviewer.displayName,
        };
      }),
    );
  }

  scrollToReviews() {
    this.anchorScroll.scrollTo(UserProfileSection.REVIEWS);
    this.featuredReviewClick.emit();
  }
}
